<template>
    <Dialog
        :value="true"
        header-text=""
        :width="920"
        persistent
        hide-close-icon
        actual-full-content-width
        fixed
        @close_modal="$emit('close')"
    >
        <template #header-toolbar>
            <div/>
        </template>
        <div style="position:relative;">
            <v-img :src="require('@/assets/referral_hero.jpeg')" height="373"/>
            <div style="position: absolute; right: 12px; top: 12px">
                <icon-button
                    color="inverted"
                    icon="X"
                    @click="$emit('close')"
                />
            </div>
        </div>
        <v-row
            no-gutters
            class="py-7"
        >
            <v-col cols="8" class="pl-10 pr-5">
                <div class="headline mb-4">{{ $t('Get {0} in cash for every referral who becomes a Uku client.').format(payAmountWithCurrency) }}</div>
                <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center">
                        <icon
                            icon="Heart"
                            color="pink"
                        />
                        <div class="ml-2 subtitle-1">
                            {{ $t('Invite a friend') }}
                        </div>
                    </div>
                    <action-button
                        icon="Copy"
                        :text-transform-normal="true"
                        @click="copyInviteLinkToClipboard"
                    >
                        {{ $t('Copy your invite link') }}
                    </action-button>
                </div>
                <div class="d-flex">
                    <div class="mr-1 flex-grow-1">
                        <text-field
                            :value="inviteName"
                            :error-messages="errors && errors.name ? errors.name : null"
                            :label="$t('Name')"
                            @input="onNameInput"
                        />
                    </div>
                    <div class="ml-1 flex-grow-1">
                        <text-field
                            :value="inviteEmail"
                            :label="$t('Email address')"
                            :error-messages="errors && errors.email ? errors.email : null"
                            @input="onEmailInput"
                        />
                    </div>
                </div>
                <div class="d-flex justify-end align-center mt-1 mb-2">
                    <secondary-button
                        color="primary"
                        class="text-none"
                        @click="isMessageVisible = !isMessageVisible"
                    >
                        {{ isMessageVisible ? $t('Hide message') : $t('View message') }}
                    </secondary-button>
                    <primary-button
                        :loading="loader.isLoading"
                        @click="onSendInvite"
                    >
                        {{ $t('Send invite') }}
                    </primary-button>
                </div>
                <v-expand-transition>
                    <div v-show="!isMessageVisible">
                        <div class="title">
                            {{ $t('Your statistics') }}
                        </div>
                        <table style="border-spacing: 8px; margin-left: -8px;">
                            <thead>
                            <tr>
                                <th></th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody v-if="statistics">
                            <tr
                                v-for="(statistic, index) in statistics"
                                :key="index"
                            >
                                <td class="font-weight-medium pr-2 text-right">
                                    {{ statistic.value }}
                                </td>
                                <td>
                                    {{ statistic.name }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </v-expand-transition>
                <v-expand-transition>
                    <div v-show="isMessageVisible">
                        <text-area-field
                            :value="contentText"
                            @input="contentText = $event"
                        />
                        <action-button
                            icon="Copy"
                            :text-transform-normal="true"
                            @click="copyMessageToClipboard"
                        >
                            {{ $t('Copy message') }}
                        </action-button>
                    </div>
                </v-expand-transition>
            </v-col>
            <v-col cols="4" class="pr-10 pl-5">
                <div class="title mb-4">
                    {{ $t('How it works') }}
                </div>
                <div>
                    <div class="d-flex mb-4">
                        <div class="list-number">1</div>
                        <div class="g5--text">
                            {{ $t("Invite your friends to Uku, and we'll give a free month of Uku - and show them how to boost their business.") }}
                        </div>
                    </div>
                    <div class="d-flex mb-4">
                        <div class="list-number">2</div>
                        <div class="g5--text">
                            {{ $t('After your referral becomes a paying Uku client, we send you a {0} gift card.').format(payAmountWithCurrency) }}
                            <a href="/api/help_link/referral_how_it_works" target="_blank">
                                {{ $t('Read more') }}
                            </a>
                        </div>
                    </div>
                </div>
                <div class="title mb-4">
                    {{ $t('More ways to earn cash') }}
                </div>
                <div class="d-flex">
                    <div class="flex-shrink-0 mr-3" style="min-width: 24px;">
                        <icon
                            icon="MessageSquareQuote"
                        />
                    </div>
                    <div class="g5--text body-2">
                        <div v-html="feedbackText"/>
                    </div>
                </div>
            </v-col>
        </v-row>
    </Dialog>
</template>

<script>
import {$t} from "@/i18n";
import {computed, onMounted, reactive, ref} from "@vue/composition-api";
import Dialog from "@/components/Dialog";
import RichTextEditor from "@/components/RichTextEditor/RichTextEditor.vue";
import AccountModule from "@/store/modules/account";
import ValidationModule from "@/modules/validation/validation";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import Loader from "@/utility/loader";
import ApiReferral from "@/api/referral/referral"
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import AppModule from "@/store/modules/app";

export default {
    name: "ReferralModal",
    components: {
        RichTextEditor,
        Dialog
    },
    setup() {
        const {baseUrl, referralPayAmount, referralPayCurrency} = AppModule()
        const {showFeedbackNotification} = FeedbackNotificationModule();

        const payAmountWithCurrency = computed(() => {
            if (referralPayCurrency.value === "$") {
                return referralPayCurrency.value + referralPayAmount.value.toString()
            } else {
                return referralPayAmount.value.toString() + referralPayCurrency.value
            }
        })

        const {current_account} = AccountModule();
        const loader = reactive(new Loader());

        const errors = ref(null)
        const inviteName = ref('')
        const inviteEmail = ref('')
        const isMessageVisible = ref(false)
        const statistics = ref(null)

        onMounted(async () => {
            try {
                statistics.value = await ApiReferral.getReferralStatistics()
            } catch (e) {
                errorDialog.showError(e)
            }
        })

        let text = $t("referral_email_content")
        if (text === "referral_email_content") {
            // handle no translation
            text = "Hi {{friend_name}},<br><br>{{accept_invite_link}}<br><br>Best regards,<br>{{your_name}}"
        }
        // translation html <br> and javascript requires linebreak
        text = text.replaceAll('{{your_name}}', current_account.value.name)
        text = text.replaceAll('<br>', '\n')
        const contentText = ref(text)

        const feedbackText = computed(() => {
            let text = $t("Write feedback for Uku and get <a href='/api/help_link/referral_feedback' target='_blank'>{0} gift card here</a>")
            return text.format(payAmountWithCurrency.value)
        })

        const onSendInvite = async () => {
            await loader.withLoader(async () => {
                try {
                    const data = await ApiReferral.sendReferralInvite({
                        email: inviteEmail.value,
                        name: inviteName.value,
                        email_body: contentText.value,
                    })

                    if (data.is_new_email && statistics.value) {
                        statistics.value = statistics.value.map(x => {
                            if (x.key === 'invited_contacts') {
                                x.value += 1
                            }

                            return x
                        })
                    }

                    showFeedbackNotification($t('Invite sent'))
                } catch (e) {
                    errors.value = ValidationModule.parseErrors(e)
                    if (!ValidationModule.hasErrors(errors.value)) {
                        errorDialog.showError(e);
                    }
                }
            })
        }

        const copyInviteLinkToClipboard = () => {
            const inviteLink = baseUrl.value + '/signup?invited_by_id=' + current_account.value.id

            navigator.clipboard.writeText(inviteLink)
            showFeedbackNotification($t('Copied to clipboard'))
        }

        const copyMessageToClipboard = () => {
            navigator.clipboard.writeText(contentText.value)
            showFeedbackNotification($t('Copied to clipboard'))
        }

        const onNameInput = (value) => {
            inviteName.value = value
            if (errors.value && errors.value.name) {
                errors.value.name = null
            }
        }

        const onEmailInput = (value) => {
            inviteEmail.value = value
            if (errors.value && errors.value.email) {
                errors.value.email = null
            }
        }

        return {
            inviteName,
            inviteEmail,
            isMessageVisible,
            feedbackText,
            contentText,
            onSendInvite,
            loader,
            errors,
            statistics,
            copyInviteLinkToClipboard,
            onNameInput,
            onEmailInput,
            payAmountWithCurrency,
            copyMessageToClipboard
        }
    }
}
</script>

<style scoped lang="scss">
.list-number {
    width: 24px;
    min-width: 24px;
    height: 24px;
    min-height: 24px;
    border: 1px solid var(--v-g5-base);
    color: var(--v-g5-base);
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 12px;
}

::v-deep .v-textarea textarea {
    height: 300px;
    font-size: 14px;
    line-height: 1.5rem;
}
</style>
